import React, { useEffect, useState } from "react";
import { X, RefreshCw, Trash2, AlertTriangle, Server, Monitor, Loader2, Download, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import * as api from "../api";
import { spiegaErrore } from "../spiegaErrore";

function quando(ts) {
  const d = new Date(ts);
  return isNaN(d) ? ts : d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

// Impostazioni → Info errori (solo admin): tutti gli errori registrati da backend e interfaccia
export default function ErroriModal({ open, onClose, versione, serverHost }) {
  const [errori, setErrori] = useState([]);
  const [totale, setTotale] = useState(0);
  const [loading, setLoading] = useState(false);
  const [da, setDa] = useState("");
  const [a, setA] = useState("");
  const [soluzioni] = useState({});

  const carica = async () => {
    setLoading(true);
    try {
      const oggi = new Date().toLocaleDateString("sv-SE");
      const [r, ro] = await Promise.all([api.getErrori({ da, a, limit: da || a ? 5000 : 200 }), api.getErrori({ da: oggi, a: oggi, limit: 1 })]);
      setErrori(r.errori || []);
      setTotale(ro.totale || 0);
    } catch (e) {
      toast.error("Impossibile leggere il registro errori");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (open) carica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, da, a]);
  if (!open) return null;

  const esporta = () => {
    const righe = visibili.map((e) => ({ ...e, soluzione: soluzioni[e.id] || e.soluzione || "" }));
    if (righe.length === 0) return toast.error("Nessun errore nel periodo scelto");
    const csv = ["Data;Origine;Livello;Fonte;Messaggio;Dettaglio;Soluzione"]
      .concat(righe.map((e) => [quando(e.ts), e.origine, e.livello, e.fonte, e.messaggio, e.dettaglio || "", e.soluzione].map((v) => `"${String(v || "").replace(/"/g, '""').replace(/\r?\n/g, " ")}"`).join(";")))
      .join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url;
    el.download = `errori_postaai_${da || "inizio"}_${a || "oggi"}.csv`;
    document.body.appendChild(el);
    el.click();
    setTimeout(() => { el.remove(); URL.revokeObjectURL(url); }, 30000);
    toast.success(`Esportati ${righe.length} errori`);
  };

  // Una riga sola per errore ripetuto: resta l'ultima occorrenza con il contatore totale
  const chiave = (e) => `${e.origine}|${e.fonte}|${(e.messaggio || "").replace(/\d+([.,:]\d+)*/g, "N").slice(0, 160)}`;
  const visibili = (() => {
    const mappa = new Map();
    for (const e of errori) {
      const k = chiave(e);
      const prec = mappa.get(k);
      if (prec) prec.ripetizioni = (prec.ripetizioni || 0) + 1 + (e.ripetizioni || 0);
      else mappa.set(k, { ...e });
    }
    return [...mappa.values()];
  })();
  const svuota = async () => {
    if (!window.confirm("Cancellare tutto il registro errori?")) return;
    await api.svuotaErrori();
    toast.success("Registro errori svuotato");
    carica();
  };

  return (
    <div className="wf-modal-backdrop show" onClick={onClose} data-testid="errori-backdrop">
      <div className="wf-modal errori-modal" onClick={(e) => e.stopPropagation()} data-testid="errori-modal">
        <div className="wf-modal-head">
          <div className="wf-modal-title"><AlertTriangle size={20} strokeWidth={2.5} /> Errori</div>
          <button className="icon-btn" onClick={onClose} aria-label="Chiudi" data-testid="errori-close"><X size={20} /></button>
        </div>
        <div className="errori-meta" data-testid="errori-meta">
          Oggi registrate <b className="errori-numero" data-testid="errori-numero">{totale}</b> {totale === 1 ? "anomalia" : "anomalie"}
        </div>
        <div className="errori-toolbar">
          <label className="errori-periodo">Dal <input type="date" className="wf-input errori-data" value={da} onChange={(e) => setDa(e.target.value)} data-testid="errori-da" /></label>
          <label className="errori-periodo">al <input type="date" className="wf-input errori-data" value={a} onChange={(e) => setA(e.target.value)} data-testid="errori-a" /></label>
          <button className="wf-btn" onClick={esporta} data-testid="errori-esporta"><Download size={15} /> Esporta</button>
          <button className="wf-btn" onClick={carica} data-testid="errori-aggiorna"><RefreshCw size={15} className={loading ? "spin" : ""} /> Aggiorna</button>
          <button className="wf-btn errori-svuota" onClick={svuota} data-testid="errori-svuota"><Trash2 size={15} /> Svuota</button>
        </div>
        <div className="errori-lista" data-testid="errori-lista">
          {loading && errori.length === 0 && <div className="errori-vuoto"><Loader2 size={20} className="spin" /> Carico...</div>}
          {!loading && visibili.length === 0 && <div className="errori-vuoto" data-testid="errori-vuoto">Nessun errore registrato</div>}
          {visibili.map((e) => (
            <div key={e.id} className={`errore-riga liv-${e.livello}`} data-testid={`errore-${e.id}`}>
              <div className="errore-top">
                {e.origine === "backend" ? <Server size={14} strokeWidth={2.4} /> : <Monitor size={14} strokeWidth={2.4} />}
                <span className="errore-quando">{quando(e.ts)}</span>
                <span className={`errore-liv liv-${e.livello}`}>{e.livello}</span>
                {e.ripetizioni > 0 && <span className="errore-rip" title={`Ripetuto fino alle ${quando(e.ts_ultimo)}`} data-testid="errore-ripetizioni">×{e.ripetizioni + 1}</span>}
                <span className="errore-fonte">{e.fonte}</span>
              </div>
              <div className="errore-msg">{e.messaggio}</div>
              {spiegaErrore(e) && <div className="errore-spiega" data-testid={`spiega-${e.id}`}>{spiegaErrore(e)}</div>}
              {e.dettaglio && <pre className="errore-dettaglio">{e.dettaglio}</pre>}
              {(soluzioni[e.id] || e.soluzione) && (
                <div className="errore-soluzione" data-testid={`soluzione-${e.id}`}><Lightbulb size={14} /> <div>{(soluzioni[e.id] || e.soluzione).replace(/\*\*/g, "")}</div></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
